import {
    useState,
    useEffect,
    Dispatch,
    SetStateAction,
    cloneElement
} from "react";

import { css, cssMap } from "@compiled/react";
import { useIntl } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";

import { Adg4DraggableTab } from "@/components/adg4/tabs/src/draggable-tab";
import { Adg4DraggableTabList } from "@/components/adg4/tabs/src/draggable-tab-list";
import { Adg4TabPanel } from "@/components/adg4/tabs/src/tab-panel";
import { useAdg4TabsContext } from "@/components/adg4/tabs/src/tabs";
import { Adg4Tabs } from "@/components/adg4/tabs/src/tabs";
import { Picture } from "@/components/wpl/image/src/image";
import { wacToken } from "@/libraries/design/foundations";
import { useAnchorLocalStorage, TeamType } from "@/utils/local-storage-hook";

import { JSW_FEATURES_TEAM_DATA } from "./gwp-4223-jira-team-tabs-content/jsw-features-team-data";

const iconWrapperStyle = css({
    width: 60,
    height: 60,
    borderRadius: "13%",
    // eslint-disable-next-line local/ensure-wac-token-usage
    backgroundColor: "rgba(255, 255, 255, 0.30)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
});

const buttonDefaultStyles = css({
    background: "none",
    border: "none",
    textAlign: "center",
    textDecoration: "none",
    cursor: "pointer",
    fontFamily: wacToken("font.charlie.text"),
    fontWeight: "400",
    display: "flex",
    flexDirection: "column",
    alignItems: "center"
});

const iconHoverStyles = css({
    "&:hover": {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ".icon-wrapper": {
            // eslint-disable-next-line local/ensure-wac-token-usage
            boxShadow: "0 8px 9pt 0 #091e4226,0 0 1px 0 #091e424f"
        }
    }
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const buttonStyles: { [key: string]: any } = cssMap({
    sm: {
        width: 96
    },
    md: {
        width: 108
    },
    lg: {
        width: 120
    },
    xl: {
        width: 120
    }
});

const selectedStyles = css({
    backgroundColor: wacToken("color.extended.blue800")
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const iconWrapperStyleMap: { [key: string]: any } = cssMap({
    sm: {
        height: 38,
        width: 38,
        padding: 10
    },
    md: {
        height: 43,
        width: 43,
        padding: 11
    },
    lg: {
        height: 48,
        width: 48,
        padding: 11
    },
    xl: {
        height: 60,
        width: 60,
        padding: 14
    }
});

const Tab = ({
    teamTypeId,
    index,
    title,
    iconSrc,
    iconSrcSelected,
    setSelectedTeamType
}: {
    teamTypeId: TeamType | undefined;
    index: number;
    title: string;
    iconSrc: string;
    iconSrcSelected?: string;
    setSelectedTeamType: Dispatch<SetStateAction<TeamType | undefined>>;
}) => {
    const { isCurrentTab } = useAdg4TabsContext();
    const isSelected = isCurrentTab(index);

    const { createAnalyticsEvent } = useAnalyticsEvents();

    // analytics for "team expansion box" experiment GWP-4223
    const handleTeamTabClick = (clickedTeamType: TeamType | undefined) => {
        setSelectedTeamType(clickedTeamType);
        if (clickedTeamType) {
            createAnalyticsEvent({
                eventName: "clickTeamSelectorTab",
                eventType: "ui",
                action: "clicked",
                actionSubject: "linkButton",
                actionSubjectId: "teamSelectorTab",
                source: "teamExpansionSelector",
                attributes: {
                    product: "wac",
                    pageUrl: window.location.href,
                    pagePath: window.location.pathname,
                    teamType: clickedTeamType
                }
            }).fire();
        }
    };

    let buttonSize;
    switch (index) {
        case 0:
        case 6:
            buttonSize = "sm";
            break;
        case 1:
        case 5:
            buttonSize = "md";
            break;
        case 2:
        case 4:
            buttonSize = "lg";
            break;
        case 3:
            buttonSize = "xl";
            break;
        default:
            buttonSize = "sm";
    }

    const buttonSizingStyles = buttonStyles[buttonSize];
    const iconWrapperSizingStyles = iconWrapperStyleMap[buttonSize];

    return (
        <button
            css={[buttonDefaultStyles, buttonSizingStyles, iconHoverStyles]}
            onClick={(e) => {
                e.preventDefault();
                handleTeamTabClick(teamTypeId);
            }}
        >
            <div
                className="icon-wrapper"
                css={[
                    iconWrapperStyle,
                    iconWrapperSizingStyles,
                    isSelected && selectedStyles
                ]}
            >
                <Picture
                    image={{
                        src:
                            isSelected && iconSrcSelected
                                ? iconSrcSelected
                                : iconSrc,
                        alt: "",
                        width: 100
                    }}
                />
            </div>
            <p>{title}</p>
        </button>
    );
};

// GWP-4226
export const PaidTeamSelectionHeroSection = ({
    keyword
}: {
    keyword?: string;
}) => {
    const intl = useIntl();

    const whiteBackgroundStyle = css({
        // eslint-disable-next-line local/ensure-wac-token-usage
        backgroundColor: "#DEEBFF"
    });

    const containerStyle = css({
        paddingBlockStart: 100,
        paddingBlockEnd: 70
    });

    const teamData = JSW_FEATURES_TEAM_DATA;

    const { setAnchorLocalStorage } = useAnchorLocalStorage();
    const [selectedTeamType, setSelectedTeamType] = useState<
        TeamType | undefined
    >();

    useEffect(() => {
        setAnchorLocalStorage({
            team: selectedTeamType
        });
    }, [setAnchorLocalStorage, selectedTeamType]);

    return (
        <div
            data-testid="hero-team-selection-variant"
            id="start-trial"
            css={whiteBackgroundStyle}
        >
            <div css={containerStyle}>
                <Adg4Tabs initialTab={3}>
                    <Adg4DraggableTabList>
                        {teamData.map((team, index) => (
                            <Adg4DraggableTab
                                key={index}
                                tabIndex={index}
                                className="round-tab"
                            >
                                <Tab
                                    index={index}
                                    title={intl.formatMessage(team.title)}
                                    iconSrc={team.icon}
                                    iconSrcSelected={team.iconSrcSelected}
                                    setSelectedTeamType={setSelectedTeamType}
                                    teamTypeId={team.teamTypeId}
                                />
                            </Adg4DraggableTab>
                        ))}
                    </Adg4DraggableTabList>
                    {teamData.map((team, index) => (
                        <Adg4TabPanel
                            panelIndex={index}
                            id={team.teamTypeId}
                            key={index}
                        >
                            {cloneElement(
                                team.tabComponent as React.ReactElement,
                                {
                                    keyword
                                }
                            )}
                        </Adg4TabPanel>
                    ))}
                </Adg4Tabs>
            </div>
        </div>
    );
};
PaidTeamSelectionHeroSection.displayName = "PaidTeamSelectionHeroSection";
